import { type DateTimeValueReturnType, getDateRangeISO, reshapeSelectedFilters, sanitizeObjectEntries } from 'BreetComponents';
import { type DropdownOptionType, getQueryParamValue, type IPagination, toISODate, updateUrlSearchParams } from 'BreetHelpers';
import { selectExportEndDate, selectExportStartDate, useAppSelector } from 'BreetRedux';
import { type ComponentType, useCallback, useEffect, useMemo, useState } from 'react';

import type { WithPaginatedFiltersProps } from './helpers';

const sortableFilters = ['createdAt', 'updatedAt'];

export const withPaginatedFilters = <P, D = object | undefined>(
	Component: ComponentType<P>,
	extraOptions: { extraSortableFilters?: string[]; initialDateRange?: DateTimeValueReturnType } = {
		extraSortableFilters: [],
		initialDateRange: getDateRangeISO(1),
	}
) => {
	const EnhacedComponent = (props: P & Partial<WithPaginatedFiltersProps<D>>) => {
		const extraSortableFilters = useMemo(() => extraOptions.extraSortableFilters ?? [], []);

		const exportStartDate = useAppSelector(selectExportStartDate);
		const exportEndDate = useAppSelector(selectExportEndDate);

		const [reqPageNum, setReqPageNum] = useState(1);
		const [reqPageSize, setReqPageSize] = useState(50);
		const [reqFilter, setReqFilter] = useState<Record<string, string>>({});
		const [reqSearch, setReqSearch] = useState<Record<string, string>>({});
		const [reqSorting, setReqSorting] = useState<Record<string, string>>({});
		const [checkedItems, setCheckedItems] = useState<D[]>([]);

		useEffect(() => {
			const page = getQueryParamValue('page');
			const size = getQueryParamValue('size');

			if (page) setReqPageNum(+page);
			if (size) setReqPageSize(+size);
		}, []);

		const handleCheckboxChange = useCallback((data: D[]) => {
			setCheckedItems(data);
		}, []);

		const handlePageChange = useCallback((state: Partial<IPagination>) => {
			setReqPageNum(state.currentPage ?? 1);
			setReqPageSize(state.pageLimit ?? 10);
			updateUrlSearchParams({ addParams: { page: state.currentPage ?? 1, size: state.pageLimit ?? 10 } });
		}, []);

		const handleSelectedFilterChange = useCallback(
			(selectedFilters?: Record<string, DropdownOptionType | undefined>) => {
				if (!selectedFilters) return;

				const sortingFilters: Record<string, DropdownOptionType> = {};
				const otherFilters: Record<string, DropdownOptionType> = {};

				Object.entries(selectedFilters).forEach(([filteredKey, filteredValue]) => {
					if (!filteredValue) return;
					if ([...sortableFilters, ...extraSortableFilters].includes(filteredKey)) sortingFilters[filteredKey] = filteredValue;
					else otherFilters[filteredKey] = filteredValue;
				});

				setReqSorting(reshapeSelectedFilters(sortingFilters));
				setReqFilter(reshapeSelectedFilters(otherFilters));

				setReqPageNum(1);
			},
			[extraSortableFilters]
		);

		const applyFilters = useCallback((values: Record<string, string>) => {
			setReqFilter(Object.fromEntries(Object.entries(values).filter(([, value]) => value)));
			setReqPageNum(1);
		}, []);

		const updateFilterData = (
			prevData: Record<string, string>,
			currentSearchKey: string,
			sanitizedData: Record<string, string>
		) => {
			const restOfPrevData = Object.fromEntries(Object.entries(prevData).filter(([key]) => key !== currentSearchKey));
			return { ...restOfPrevData, ...sanitizedData } as Record<string, string>;
		};

		const handleReqSearch = useCallback((data: Record<string, string>) => {
			const sanitizedData = sanitizeObjectEntries(data);
			const searchKeys = Object.keys(data);
			const [currentSearchKey] = searchKeys;
			const strictFilterKeys = ['_id'];
			const searchIncludesFilterKey = searchKeys.some((key) => strictFilterKeys.includes(key));

			const encodedSearch = Object.entries(sanitizedData).reduce<Record<string, string>>((acc, [key, value]) => {
				acc[key] = encodeURIComponent(value as string);
				return acc;
			}, {});

			if (searchIncludesFilterKey) {
				setReqFilter((prevData) => updateFilterData(prevData, currentSearchKey, sanitizedData as Record<string, string>));
			} else {
				setReqSearch(encodedSearch);
			}

			setReqPageNum(1);
		}, []);

		const exportsQuery = useMemo(
			() => ({
				startDate: exportStartDate ? toISODate(exportStartDate) : '',
				endDate: exportEndDate ? toISODate(exportEndDate) : '',
				exports: !!exportStartDate && !!exportEndDate,
			}),
			[exportEndDate, exportStartDate]
		);

		return (
			<Component
				{...props}
				reqPageNum={reqPageNum}
				reqPageSize={reqPageSize}
				reqFilter={reqFilter}
				reqSearch={reqSearch}
				reqSorting={reqSorting}
				checkedItems={checkedItems}
				handleCheckboxChange={handleCheckboxChange}
				handlePageChange={handlePageChange}
				handleSelectedFilterChange={handleSelectedFilterChange}
				applyFilters={applyFilters}
				handleReqSearch={handleReqSearch}
				exportsQuery={exportsQuery}
			/>
		);
	};

	return EnhacedComponent;
};
